/**
 * StageWorldView — sky, water, girders, ladders (broken ones as ghost rungs
 * with build progress), turbines and Kong-Bot. Redrawn from sim state.
 */

import { toSub, WORLD_W_PX, WORLD_H_PX } from "../../shared/stage/physics";
import { toScreen } from "./stageLayout";

const GIRDER_PX = 6;
const LADDER_W_PX = 10;

export function createWorldView(scene) {
  return {
    sky: scene.add.graphics().setDepth(0),
    world: scene.add.graphics().setDepth(10),
    turbineSpin: 0,
  };
}

function rect(g, layout, xPx, yPx, wPx, hPx) {
  const { x, y } = toScreen(layout, toSub(xPx), toSub(yPx));
  g.fillRect(x, y, wPx * layout.scale, hPx * layout.scale);
}

function drawSky(view, layout, pal, sim) {
  const g = view.sky;
  g.clear();
  const bands = 16;
  for (let i = 0; i < bands; i++) {
    const t = i / (bands - 1);
    const mix = (a, b) => Math.round(a + (b - a) * t);
    const color =
      (mix(pal.skyTop >> 16, pal.skyBottom >> 16) << 16) |
      (mix((pal.skyTop >> 8) & 0xff, (pal.skyBottom >> 8) & 0xff) << 8) |
      mix(pal.skyTop & 0xff, pal.skyBottom & 0xff);
    g.fillStyle(color, 1);
    rect(g, layout, 0, (WORLD_H_PX / bands) * i, WORLD_W_PX, WORLD_H_PX / bands + 1);
  }
  // Sun (solarpunk) / neon moon
  const sun = toScreen(layout, toSub(196), toSub(20));
  g.fillStyle(pal.sun, sim.kind === "build" ? 0.9 : 0.5);
  g.fillCircle(sun.x, sun.y, 12 * layout.scale);
}

function drawWater(g, layout, pal, sim) {
  // Build stages: the flood rises as the energy reserve (bonus timer) runs out
  const bottom = sim.config.tiersY[0] + 8;
  const frac =
    sim.kind === "build" && sim.config.reserveWh > 0
      ? 1 - sim.reserveWh / sim.config.reserveWh
      : Math.min(1, sim.tick / sim.config.surviveTicks);
  const maxRise = bottom - sim.config.tiersY[2];
  const top = bottom - 6 - frac * maxRise;
  g.fillStyle(pal.water, sim.kind === "build" ? 0.45 : 0.25);
  rect(g, layout, 0, top, WORLD_W_PX, WORLD_H_PX - top);
}

function drawLadder(g, layout, pal, sim, ladder) {
  const x = ladder.x / 16 - LADDER_W_PX / 2;
  const top = sim.config.tiersY[ladder.to];
  const bottom = sim.config.tiersY[ladder.from];
  const span = bottom - top;
  const built = ladder.rungs / ladder.need;
  const complete = ladder.rungs >= ladder.need;
  const railColor = complete ? pal.ladder : pal.ladderGhost;
  g.fillStyle(railColor, complete ? 1 : 0.6);
  rect(g, layout, x, top, 1.5, span);
  rect(g, layout, x + LADDER_W_PX - 1.5, top, 1.5, span);
  // Rungs from the bottom up; missing ones drawn as faint ghosts
  for (let i = 0; i < ladder.need; i++) {
    const y = bottom - ((i + 1) * span) / (ladder.need + 1);
    const present = i < ladder.rungs;
    g.fillStyle(present ? pal.ladder : pal.ladderGhost, present ? 1 : 0.35);
    rect(g, layout, x, y, LADDER_W_PX, 1.5);
  }
  if (!complete) {
    // Build progress bar beside the ladder
    g.fillStyle(0x000000, 0.5);
    rect(g, layout, x + LADDER_W_PX + 2, bottom - span, 2, span);
    g.fillStyle(ladder.cracked ? 0xff3333 : 0xffd84a, 1);
    rect(g, layout, x + LADDER_W_PX + 2, bottom - span * built, 2, span * built);
  }
}

function drawTurbine(g, layout, pal, spin, xPx, yPx, active) {
  const c = toScreen(layout, toSub(xPx), toSub(yPx));
  const r = 7 * layout.scale;
  g.lineStyle(Math.max(1, layout.scale), pal.turbine, active ? 1 : 0.4);
  for (let i = 0; i < 3; i++) {
    const a = spin + (i * Math.PI * 2) / 3;
    g.lineBetween(c.x, c.y, c.x + Math.cos(a) * r, c.y + Math.sin(a) * r);
  }
  g.fillStyle(pal.turbineHub, 1);
  g.fillCircle(c.x, c.y, 1.5 * layout.scale);
}

function drawBoss(g, layout, pal, sim) {
  const { x, tier } = sim.config.boss;
  const y = sim.config.tiersY[tier];
  g.fillStyle(pal.boss, 1);
  rect(g, layout, x - 12, y - 22, 24, 22);
  rect(g, layout, x - 16, y - 16, 4, 12);
  rect(g, layout, x + 12, y - 16, 4, 12);
  g.fillStyle(pal.bossEye, 1);
  rect(g, layout, x - 7, y - 17, 5, 3);
  rect(g, layout, x + 2, y - 17, 5, 3);
}

export function renderWorld(view, layout, pal, sim, deltaMs) {
  drawSky(view, layout, pal, sim);
  const g = view.world;
  g.clear();
  drawWater(g, layout, pal, sim);

  sim.config.tiersY.forEach((y, tier) => {
    g.fillStyle(pal.girder, 1);
    rect(g, layout, 0, y, WORLD_W_PX, GIRDER_PX);
    g.fillStyle(pal.girderTrim, 1);
    rect(g, layout, 0, y, WORLD_W_PX, 1.5);
    // Rivets every 16px, like Donkey Kong girders
    g.fillStyle(pal.girderTrim, 0.6);
    for (let x = 4; x < WORLD_W_PX; x += 16) rect(g, layout, x, y + 3, 2, 2);
    if (tier === sim.config.goalTier && sim.kind === "build") {
      g.fillStyle(0x7bc74a, 0.5);
      rect(g, layout, 96, y - 3, 32, 3); // the dam's control deck (goal)
    }
  });

  sim.ladders.forEach((ladder) => drawLadder(g, layout, pal, sim, ladder));

  view.turbineSpin += (deltaMs / 1000) * 6;
  // Build stages: a turbine spins on top of every ladder you built
  sim.ladders
    .filter((l) => l.need > 0 && l.rungs >= l.need && sim.config.ladders.find((c) => c.id === l.id).rungs < l.need)
    .forEach((l) => drawTurbine(g, layout, pal, view.turbineSpin, l.x / 16 + 12, sim.config.tiersY[l.to] - 10, true));
  sim.turbines.forEach((tb) => {
    const leeched = sim.leeches.some((lc) => lc.turbine === tb.id);
    drawTurbine(g, layout, pal, leeched ? view.turbineSpin * 0.2 : view.turbineSpin, tb.x / 16, sim.config.tiersY[tb.tier] - 10, !leeched);
  });

  drawBoss(g, layout, pal, sim);
}
